import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  Group as GroupIcon,
  Class as ClassIcon,
  Subject as SubjectIcon,
  AdminPanelSettings as RolesIcon,
  Grade as GradeIcon,
  Assessment,
  AccessTime,
  EventBusy,
  Policy,
  HolidayVillage,
  Public,
  Category as CategoryIcon,
  MailOutline,
  FactCheck,
} from '@mui/icons-material';
import { useSubscription } from '../context/SubscriptionContext';
import { useModulePermissions } from '../hooks/useModulePermissions';
import SettingsShell, { SettingsShellItem } from '../components/common/SettingsShell';
import GroupsSettings from '../components/Settings/GroupsSettings';
import ClassesSettings from '../components/Settings/ClassesSettings';
import SubjectsSettings from '../components/Settings/SubjectsSettings';
import GradesSettings from '../components/Settings/GradesSettings';
import ResultsSettings from '../components/Settings/ResultsSettings';
import RolesSettingsSimplified from '../components/Settings/RolesSettingsSimplified';
import ExamTypesSettings from '../components/Settings/ExamTypesSettings';
import LetterCategoriesSettings from '../components/Settings/LetterCategoriesSettings';
import AttendanceSettings from '../components/Settings/AttendanceSettings';
import LeaveTypesSettings from '../components/Settings/LeaveTypesSettings';
import LeavePolicySettings from '../components/Settings/LeavePolicySettings';
import HolidaySettings from '../components/Settings/HolidaySettings';
import EventCategorySettings from '../components/Settings/EventCategorySettings';
import SchoolSettings from '../components/Settings/SchoolSettings';
import { FINANCE_SETUP_IDS, FinanceSetupId } from '../components/finance/setup/useFinanceSetupItems';

type Item = SettingsShellItem & { visible: boolean };

/**
 * Settings, grouped in the order a school sets itself up. Every item has
 * its own granular scope: a role granted `settings_grades` sees Grades and
 * nothing else. An item without a gate is never shown, so a new sub-page
 * must register its own settings_* module and gate here.
 */
const SettingsPage: React.FC = () => {
  const [params] = useSearchParams();
  const { canRead } = useModulePermissions();
  const { hasFeature } = useSubscription();

  // Money settings moved to Finance → Setup; keep old links working.
  const requested = params.get('item');
  if (requested && FINANCE_SETUP_IDS.includes(requested as FinanceSetupId)) {
    return <Navigate to={`/app/finance/setup?item=${requested}`} replace />;
  }

  const all: Item[] = [
    {
      id: 'regional', group: 'School', title: 'Regional preferences',
      description: 'Timezone, date format, currency, the academic year and the fee billing day.',
      icon: <Public />, render: () => <SchoolSettings />,
      visible: canRead('settings_school_config'),
    },
    {
      id: 'roles', group: 'School', title: 'Roles & permissions',
      description: 'Who can see and do what. System roles are fixed; your own roles are fully editable.',
      icon: <RolesIcon />, render: () => <RolesSettingsSimplified />,
      visible: canRead('settings_roles'),
    },
    {
      id: 'classes', group: 'Academics', title: 'Classes',
      description: 'The classes students move through, in promotion order. The last is the terminal class.',
      icon: <ClassIcon />, render: () => <ClassesSettings />,
      visible: canRead('settings_classes'),
    },
    {
      id: 'groups', group: 'Academics', title: 'Student groups',
      description: 'Streams such as Pre-Engineering or Pre-Medical that students are placed in.',
      icon: <GroupIcon />, render: () => <GroupsSettings />,
      visible: canRead('settings_groups'),
    },
    {
      id: 'subjects', group: 'Academics', title: 'Subjects',
      description: 'Every subject taught at the school.',
      icon: <SubjectIcon />, render: () => <SubjectsSettings />,
      visible: canRead('settings_subjects'),
    },
    {
      id: 'grades', group: 'Academics', title: 'Grades',
      description: 'The grading scale: which percentage earns which grade.',
      icon: <GradeIcon />, render: () => <GradesSettings />,
      visible: canRead('settings_grades'),
    },
    {
      id: 'results', group: 'Academics', title: 'Results',
      description: 'How an exam result is decided: absent papers, the failing grade and what passing needs.',
      icon: <FactCheck />, render: () => <ResultsSettings />,
      visible: canRead('settings_results'),
    },
    {
      id: 'exam-types', group: 'Academics', title: 'Exam types',
      description: 'Kinds of exams the school holds, such as Monthly Test, Midterm or Final.',
      icon: <Assessment />, render: () => <ExamTypesSettings />,
      visible: hasFeature('examination') && canRead('settings_exam_types'),
    },
    {
      id: 'holidays', group: 'Calendar', title: 'Holidays',
      description: 'Declared holidays and off-days. Attendance and leave skip them.',
      icon: <HolidayVillage />, render: () => <HolidaySettings />,
      visible: canRead('settings_holidays'),
    },
    {
      id: 'event-categories', group: 'Calendar', title: 'Event categories',
      description: 'How school events are grouped, such as Sports, Academic or Cultural.',
      icon: <CategoryIcon />, render: () => <EventCategorySettings />,
      visible: hasFeature('events') && canRead('settings_event_categories'),
    },
    {
      id: 'employee-attendance-settings', group: 'Staff', title: 'Employee attendance',
      description: 'Working hours and when an arrival counts as late or a half day.',
      icon: <AccessTime />, render: () => <AttendanceSettings />,
      visible: hasFeature('attendance') && canRead('employee_attendance'),
    },
    {
      id: 'leave-types', group: 'Staff', title: 'Leave types',
      description: 'Kinds of leave employees can take, such as Sick, Casual or Annual.',
      icon: <EventBusy />, render: () => <LeaveTypesSettings />,
      visible: hasFeature('leaves') && canRead('settings_leave_types'),
    },
    {
      id: 'leave-policies', group: 'Staff', title: 'Leave policies',
      description: 'How many days of each leave type each role gets per year.',
      icon: <Policy />, render: () => <LeavePolicySettings />,
      visible: hasFeature('leaves') && canRead('settings_leave_types'),
    },
    {
      id: 'letter-categories', group: 'Students', title: 'Letter categories',
      description: 'Letters you can issue to students, such as Commendation, Warning or Fine.',
      icon: <MailOutline />, render: () => <LetterCategoriesSettings />,
      visible: hasFeature('letters') && canRead('settings_letter_categories'),
    },
  ];

  return (
    <SettingsShell
      title="Settings"
      subtitle="How your school is set up. Start at the top and work down."
      items={all.filter(i => i.visible)}
      emptyDescription="Your role doesn't include any Settings area. If your administrator just updated your role, sign out and back in to pick it up."
    />
  );
};

export default SettingsPage;
